/**
 * Búfer de interpolación de snapshots (sin dependencias del DOM: se testea en Node).
 *
 * El cliente online muestra el mundo `delayTicks` por detrás del último snapshot
 * recibido e interpola entre los dos que rodean ese instante. El reloj de
 * reproducción avanza en tiempo real y se acelera o frena levemente (hasta
 * ±30%) para mantener ese margen pese a la variación de llegada de los paquetes;
 * si se desfasa demasiado, salta.
 *
 * Retraso adaptativo: si `push` recibe la hora de llegada, el margen se ajusta
 * entre `minDelayTicks` y `maxDelayTicks` según cuánto se retrasan los paquetes
 * (percentil 97 de los últimos ~3 s). Sube en cuanto hace falta y baja de uno en
 * uno tras 3 s de red estable, así no hay tirones al cambiar.
 */
export interface TickedSnapshot {
  tick: number;
}

export interface PlaybackFrame<S extends TickedSnapshot> {
  /** snapshot de partida de la interpolación */
  from: S;
  /** snapshot de llegada (el que se aplica a la simulación) */
  to: S;
  /** 0..1 entre `from` y `to` */
  alpha: number;
  /** snapshots nuevos desde el frame anterior (en orden), para procesar sus eventos */
  passed: S[];
}

export class SnapshotPlayback<S extends TickedSnapshot> {
  private readonly buffer: S[] = [];
  private playback: number | null = null;
  private appliedTick = -1;
  /** llegadas recientes (tick, ms) para medir el retraso de los paquetes */
  private readonly arrivals: Array<{ tick: number; at: number }> = [];
  private delay: number;
  private pressureAt = 0;

  constructor(
    private readonly tickRate: number,
    private readonly maxDelayTicks: number,
    private readonly snapThreshold = 15,
    private readonly minDelayTicks = maxDelayTicks,
  ) {
    this.delay = maxDelayTicks;
  }

  /** margen actual (ticks por detrás del último snapshot) */
  get delayTicks(): number {
    return this.delay;
  }

  get size(): number {
    return this.buffer.length;
  }

  /** ticks entre el último snapshot recibido y lo que se está mostrando */
  get lag(): number | null {
    const last = this.buffer[this.buffer.length - 1];
    return last && this.playback !== null ? last.tick - this.playback : null;
  }

  reset(): void {
    this.buffer.length = 0;
    this.playback = null;
    this.appliedTick = -1;
    this.arrivals.length = 0;
    this.delay = this.maxDelayTicks;
    this.pressureAt = 0;
  }

  /** `arrivedAt` (ms, p. ej. `performance.now()`) activa el retraso adaptativo. */
  push(snapshot: S, arrivedAt?: number): void {
    const last = this.buffer[this.buffer.length - 1];
    if (last && snapshot.tick <= last.tick) {
      // un tick mucho menor = la partida se reinició (rematch)
      if (snapshot.tick < last.tick - 30) this.reset();
      else return;
    }
    this.buffer.push(snapshot);
    if (this.buffer.length > 60) this.buffer.shift();
    if (arrivedAt !== undefined) this.adaptDelay(snapshot.tick, arrivedAt);
  }

  private adaptDelay(tick: number, at: number): void {
    if (this.minDelayTicks >= this.maxDelayTicks) return;
    const arrivals = this.arrivals;
    arrivals.push({ tick, at });
    const window = Math.round(this.tickRate * 3);
    if (arrivals.length > window) arrivals.shift();
    if (arrivals.length < this.tickRate) return;
    // retraso de cada paquete respecto al que llegó más rápido de la ventana
    const tickMs = 1000 / this.tickRate;
    const offsets = arrivals.map((a) => a.at - a.tick * tickMs);
    const best = Math.min(...offsets);
    const late = offsets.map((o) => o - best).sort((a, b) => a - b);
    const p97 = late[Math.floor((late.length - 1) * 0.97)];
    // 1 tick para tener entre qué interpolar + lo que se retrasan (con 8 ms de colchón)
    const wanted = Math.max(this.minDelayTicks, Math.min(this.maxDelayTicks, 1 + Math.ceil((p97 + 8) / tickMs)));
    if (wanted >= this.delay) {
      this.delay = wanted;
      this.pressureAt = at;
    } else if (at - this.pressureAt > 3000) {
      this.delay--;
      this.pressureAt = at;
    }
  }

  /** Avanza el reloj de reproducción `dt` segundos y devuelve qué mostrar. */
  update(dt: number): PlaybackFrame<S> | null {
    const buffer = this.buffer;
    if (!buffer.length) return null;
    const target = buffer[buffer.length - 1].tick - this.delay;
    if (this.playback === null || Math.abs(target - this.playback) > this.snapThreshold) {
      this.playback = target;
    } else {
      const drift = target - this.playback;
      const rate = 1 + Math.max(-0.3, Math.min(0.3, drift * 0.1));
      this.playback += dt * this.tickRate * rate;
    }
    const playback = this.playback;

    let bIndex = buffer.findIndex((s) => s.tick > playback);
    if (bIndex === -1) bIndex = buffer.length - 1;
    const to = buffer[bIndex];
    const from = bIndex > 0 ? buffer[bIndex - 1] : to;

    const passed = to.tick !== this.appliedTick
      ? buffer.filter((s) => s.tick > this.appliedTick && s.tick <= to.tick)
      : [];
    this.appliedTick = to.tick;
    while (buffer.length > 2 && buffer[0].tick < from.tick) buffer.shift();

    const span = to.tick - from.tick;
    const alpha = span > 0 ? Math.max(0, Math.min(1, (playback - from.tick) / span)) : 1;
    return { from, to, alpha, passed };
  }
}
